import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Card, Image, Result, Spin, Tag } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import { productTracesApi } from '@/services/api';
import type { ProductTrace } from '@/types';
import PublicLayout, { PublicLogo } from '@/components/PublicLayout';
import './styles/PublicQuery.css';

function formatDate(value: string) {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('zh-CN');
}

function ProductTracePublicPage() {
  const { serialNumber = '' } = useParams<{ serialNumber: string }>();
  const [trace, setTrace] = useState<ProductTrace | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadTrace = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await productTracesApi.publicQuery(serialNumber);
        setTrace(data);
      } catch (err: any) {
        setError(err?.response?.data?.message || err.message || '查询失败');
      } finally {
        setLoading(false);
      }
    };

    if (serialNumber) {
      loadTrace();
    }
  }, [serialNumber]);

  if (loading) {
    return (
      <PublicLayout>
        <Card className="query-card" bordered={false}>
          <div className="loading-container">
            <Spin size="large" />
            <p>正在加载产品溯源信息...</p>
          </div>
        </Card>
      </PublicLayout>
    );
  }

  if (error || !trace) {
    return (
      <PublicLayout>
        <Card className="query-card" bordered={false}>
          <Result
            icon={<CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '72px' }} />}
            title="产品溯源不存在"
            subTitle={error || '请检查二维码是否正确'}
          />
        </Card>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <Card className="result-card show" bordered={false}>
        <div className="result-header">
          <PublicLogo />
        </div>
        <Result
          icon={
            trace.isActive ? (
              <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '72px' }} />
            ) : (
              <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '72px' }} />
            )
          }
          title="产品溯源信息"
          subTitle={trace.isActive ? '该产品溯源信息有效' : '该产品溯源信息已停用'}
        />

        <div className="result-details">
          <div className="detail-item">
            <span className="label">企业名称</span>
            <span className="value">{trace.companyName}</span>
          </div>
          <div className="detail-item">
            <span className="label">地址</span>
            <span className="value">{trace.companyAddress}</span>
          </div>
          <div className="detail-item">
            <span className="label">电话</span>
            <span className="value">{trace.companyPhone}</span>
          </div>
          <div className="detail-item detail-item-block">
            <span className="label">设备信息</span>
            <span className="value value-block">{trace.deviceInfo}</span>
          </div>
          <div className="detail-item">
            <span className="label">质保期</span>
            <span className="value">{trace.warrantyPeriod}</span>
          </div>
          <div className="detail-item">
            <span className="label">出厂日期</span>
            <span className="value">{formatDate(trace.manufactureDate)}</span>
          </div>
          <div className="detail-item">
            <span className="label">产品序列号</span>
            <span className="value serial">{trace.serialNumber}</span>
          </div>
          {trace.officialWebsite && (
            <div className="detail-item">
              <span className="label">官网链接</span>
              <span className="value">
                <Button
                  type="link"
                  href={trace.officialWebsite}
                  target="_blank"
                  rel="noreferrer"
                  icon={<GlobalOutlined />}
                  style={{ padding: 0 }}
                >
                  访问官网
                </Button>
              </span>
            </div>
          )}
          {trace.wechatQrCode && (
            <div className="detail-item detail-item-block product-trace-wechat-qr">
              <span className="label">公众号二维码</span>
              <div className="product-trace-wechat-qr-stage">
                <Image src={trace.wechatQrCode} alt="公众号二维码" width={160} />
              </div>
            </div>
          )}
          <div className="detail-item">
            <span className="label">状态</span>
            <span className="value status">
              <Tag color={trace.isActive ? 'green' : 'red'}>{trace.isActive ? '有效' : '停用'}</Tag>
            </span>
          </div>
        </div>
      </Card>
    </PublicLayout>
  );
}

export default ProductTracePublicPage;
